import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { CloseIcon } from "../../assets/Icons";
import axios from "axios";
import Select from "react-select";
import { Country } from "country-state-city";

const SiteModal = ({ show, setShow, onSave = () => {}, siteId = "", data = null }) => {
  const [siteName, setSiteName] = useState("");
  const [obrOrg, setObrOrg] = useState("");
  const [email, setEmail] = useState("");
  const [obrBu, setObrBu] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("");

  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);

  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);

  const countryOptions = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  useEffect(() => {
    if (data) {
      setSiteName(data.name || "");
      setObrOrg(data.obrOrg || "");
      setObrBu(data.obrBu || "");
      setSelectedCountry(data.country ? { value: data.country, label: data.country } : null);
      setSelectedRegion(data.region || "");
      setEmail(data.email || "");
    } else {
      setSiteName("");
      setObrOrg("");
      setObrBu("");
      setEmail("");
      setSelectedCountry(null);
      setSelectedRegion("");
    }
    setErrors({});
  }, [data, show]);

  const validate = () => {
    const newErrors = {};
    if (!siteName.trim()) newErrors.siteName = "Site name is required.";
    if (!obrOrg.trim()) newErrors.obrOrg = "OBR Org is required.";
    if (!obrBu.trim()) newErrors.obrBu = "OBR BU is required.";
    if (!selectedCountry) newErrors.country = "Country is required.";
    if (email && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Email is not valid.";
    }
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setBtnLoading(true);
    try {
      const payload = {
        name: siteName,
        obrOrg,
        obrBu,
        country: selectedCountry?.label || "",
        region: selectedRegion || "",
        data: {
          email: email.trim(),
        },
      };

      if (data) {
        await axios.put(`${configData.config.API_URL}/api/site/subsite/update/${data.uuid}`, payload, {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        });
      } else {
        await axios.post(`${configData.config.API_URL}/api/site/subsite/create/${siteId}`, payload, {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        });
      }

      setShow(false);
      onSave();
    } catch (err) {
      console.error("Error saving site:", err);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setSiteName("");
    setObrOrg("");
    setObrBu("");
    setSelectedCountry(null);
    setSelectedRegion("");
    setErrors({});
  };

  return (
    <Modal show={show} size="md" className="sitelist" centered onHide={handleClose}>
      <Modal.Header className="modalheader">
        {data ? "Update Site" : "Add Site"}
        <span onClick={handleClose}>
          <CloseIcon />
        </span>
      </Modal.Header>

      <Modal.Body>
        <div className="row px-3">
          {/* Site Name */}
          <div className="col-12 standardinput">
            <label>
              <p className="labelform">
                Site Name <span className="text-danger">*</span>
              </p>
              <input
                type="text"
                className="input form-control"
                value={siteName}
                onChange={(e) => {
                  setSiteName(e.target.value);
                  setErrors({ ...errors, siteName: null });
                }}
                placeholder="Enter Site Name"
              />
              {errors.siteName && <p className="error text-danger">{errors.siteName}</p>}
            </label>
          </div>

          {/* OBR Org */}
          <div className="col-12 standardinput">
            <label>
              <p className="labelform">
                OBR Org <span className="text-danger">*</span>
              </p>
              <input
                type="text"
                className="form-control"
                value={obrOrg}
                onChange={(e) => {
                  setObrOrg(e.target.value);
                  setErrors({ ...errors, obrOrg: null });
                }}
                placeholder="Enter OBR Org"
              />
              {errors.obrOrg && <p className="error text-danger">{errors.obrOrg}</p>}
            </label>
          </div>

          {/* OBR BU */}
          <div className="col-12 standardinput">
            <label>
              <p className="labelform">
                OBR BU <span className="text-danger">*</span>
              </p>
              <input
                type="text"
                className="form-control"
                value={obrBu}
                onChange={(e) => {
                  setObrBu(e.target.value);
                  setErrors({ ...errors, obrBu: null });
                }}
                placeholder="Enter OBR BU"
              />
              {errors.obrBu && <p className="error text-danger">{errors.obrBu}</p>}
            </label>
          </div>
          <div className="col-12 standardinput">
            <label>
              <p className="labelform">Email</p>
              <input
                type="text"
                className="form-control"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors({ ...errors, email: null });
                }}
                placeholder="Enter Email"
              />
              {errors.email && <p className="error text-danger">{errors.email}</p>}
            </label>
          </div>
          {/* Region */}
          <div className="col-12 standardinput">
            <label>
              <p className="labelform">Region</p>
              <input
                type="text"
                className="form-control"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                placeholder="Enter Region"
              />
            </label>
          </div>

          {/* Country */}
          <div className="col-12  standardinput">
            <label>
              <p className="labelform">
                Country <span className="text-danger">*</span>
              </p>
            </label>
            <Select
              options={countryOptions}
              value={selectedCountry}
              onChange={(selected) => {
                setSelectedCountry(selected);
                setErrors({ ...errors, country: null });
              }}
              placeholder="Select Country"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              className="react-select-country"
              classNamePrefix="react-select-country"
              menuPosition="fixed"
            />
            {errors.country && <p className="error text-danger">{errors.country}</p>}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="footerbtn pt-0">
        <button className="cancel" onClick={handleClose}>
          Cancel
        </button>
        {btnLoading ? (
          <button className="loading save">
            <svg viewBox="25 25 50 50">
              <circle r="20" cy="50" cx="50"></circle>
            </svg>
          </button>
        ) : (
          <button className="save" onClick={handleSave}>
            {data ? "Update" : "Save"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SiteModal;
